// SoilButtonComponent.tsx
import { useState, type FC } from "react";
import { Button, Tooltip } from "antd";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ReactComponent as SoilLIcon } from "../../assets/images/soil-btn-layers.svg";
import { SoilLayerComponent } from "./SoilLayerComponent";
import { CloseOutlined } from "@ant-design/icons";
import { removeSoilImageLayer } from "../../maps/utils/soilData";
import type mapboxgl from "mapbox-gl";
import { useNewTrialState } from "../../pages/new-trail/hooks/NewTrialStateProvider";
import { formatTranslation } from "../utils/translationUtils";
import { LayerOption } from "../../pages/new-trail/trialMapComponent/MapLayersDrawer";

interface SoilButtonProps {
  onOpen?: (layer: LayerOption) => void;
  onClose?: () => void;
  onClearSelection?: () => void;
  isSelected?: boolean;
}

const SoilButtonComponent: FC<SoilButtonProps> = ({
  onOpen,
  onClose,
  onClearSelection,
  isSelected = false,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState<boolean>(false);

  const showDrawer = (): void => {
    setVisible(true);
    onOpen?.(LayerOption.Soil);
  };
  const { setSoilOperationsData } = useNewTrialState();

  const hideSoilLayerDrawer = (): void => {
    setVisible(false);
    onClose?.();
  };

  const closeSoilLayerDrawer = (): void => {
    hideSoilLayerDrawer();
    // Remove soil layer from map if present
    // Try to get the mapboxgl.Map instance from the global mapRef (used in parent)
    let map: mapboxgl.Map | null = null;
    if (
      globalThis &&
      (globalThis as any).mapRef &&
      typeof (globalThis as any).mapRef.getMap === "function"
    ) {
      map = (globalThis as any).mapRef.getMap();
    }
    if (map) {
      removeSoilImageLayer(map);
    }
    // Clear soil data so the effect does not re-add the layer
    setSoilOperationsData(undefined);
  };

  return (
    <div>
      <div className="corner-btn soil-analysis-btn">
        <Tooltip
          title={formatTranslation(t("soil.clickToSeeSoilAnalysis"))}
          placement="left"
        >
          <Button
            ghost={true}
            aria-label={formatTranslation(t("soil.soilAnalysis"))}
            onClick={showDrawer}
          >
            <SoilLIcon className={isSelected ? "active" : ""} />
          </Button>
        </Tooltip>
        {visible && isSelected && (
          <Button
            className="close-button"
            onClick={(e) => {
              e.stopPropagation();
              closeSoilLayerDrawer();
              onClose?.();
            }}
          >
            <CloseOutlined />
          </Button>
        )}
      </div>
      {visible &&
        isSelected &&
        createPortal(
          <SoilLayerComponent closeDrawer={closeSoilLayerDrawer} />,
          document.getElementById("trial-map") ?? document.body
        )}
    </div>
  );
};

export default SoilButtonComponent;
